import {
  Alert,
  Button,
  Card,
  Form,
  FormProps,
  Icons,
  Input,
  Modal,
  ModalProps,
  Radio,
  Select,
  Space,
  Table,
  TextField,
  useSelect,
} from '@pankod/refine-antd'
import React, { useState } from 'react'

import ModalParticipantCandidateNPSN from '@resources/pendaftaran-diklat/pengajuan-peserta-didik/block/ModalParticipantCandidateNPSN'
import UploadSupportingDocument from '@resources/pendaftaran-diklat/pengajuan-peserta-didik/block/UploadSupportingDocument'
import { phone } from 'src/helpers/validator'
import useTableWithMeta, {
  TableQueryResultMeta,
} from 'src/hooks/useTableWithMeta'
import {
  Candidate,
  DiklatProgram,
  SUMBER_DATA_DIKLAT,
  SumberDataDiklat,
} from '@resources/pendaftaran-diklat/types'
import { TCommonError } from 'src/interfaces/common'
import { PROGRAM_LOCAL_STATE } from '@resources/lms-management/program-lokal/types'
import ModalParticipantCandidateDinas from '@resources/pendaftaran-diklat/pengajuan-peserta-didik/block/ModalParticipantCandidateDinas'

type ModalFormAddCalonPesertaDidik = {
  modalProps: ModalProps
  formProps: FormProps
  onClose: () => void
  onSuccess?: () => void
}

const getRegisteredNuptk = ({
  candidates,
  registeredParticipants,
}: {
  candidates: {
    nama: string
    email: string
    nuptk: string
    fileUrl?: string
  }[]
  registeredParticipants: TableQueryResultMeta<Candidate, {}>
}): string | null => {
  const registeredParticipantNuptks = new Set(
    (
      registeredParticipants?.data?.data?.filter?.(
        ({ registered }) => registered,
      ) || []
    )?.map((data) => data?.nuptk) || [],
  )

  for (const candidate of candidates) {
    if (!candidate?.nuptk) continue
    if (registeredParticipantNuptks.has(candidate?.nuptk)) {
      return candidate.nuptk
    }
  }

  return null
}

const ModalFormAddCalonPesertaDidik = ({
  formProps,
  modalProps,
  onClose,
  onSuccess,
}: ModalFormAddCalonPesertaDidik) => {
  const selectedProgramId = Form.useWatch('programId', formProps.form)
  const selectedSource = Form.useWatch('sumberData', formProps.form)
  const selectedNPSN = Form.useWatch('npsn', formProps.form)
  const selectedLembagaId = Form.useWatch('lembagaId', formProps.form)
  const [showCandidateModal, setShowCandidateModal] =
    useState<SumberDataDiklat | null>(null)
  const [isUploading, setIsUploading] = useState<Set<React.Key>>(new Set())
  const {
    tableProps: dapodikTableProps,
    tableQueryResult: dapodikTableQueryResult,
  } = useTableWithMeta<Candidate, TCommonError, unknown>({
    resource: 'diklat/calon-peserta-diklat',
    queryOptions: {
      enabled:
        showCandidateModal === SUMBER_DATA_DIKLAT.DAPODIK &&
        !!selectedProgramId &&
        !!selectedNPSN,
      select(data) {
        return {
          ...data,
          data: data?.data?.map?.((item) => ({
            ...item,
            key: `${item.nuptk}_${item.email}_${item.nama}`,
          })),
        }
      },
    },
    permanentFilter: [
      {
        field: 'pageSize',
        operator: 'eq',
        value: -1,
      },
      {
        field: 'programId',
        operator: 'eq',
        value: selectedProgramId,
      },
      {
        field: 'npsn',
        operator: 'eq',
        value: selectedNPSN,
      },
    ],
  })
  const {
    tableProps: simtendikTableProps,
    tableQueryResult: simtendikTableQueryResult,
  } = useTableWithMeta<Candidate, TCommonError, unknown>({
    resource: 'diklat/calon-peserta-pengawas-sekolah',
    queryOptions: {
      enabled:
        showCandidateModal === SUMBER_DATA_DIKLAT.SIMTENDIK &&
        !!selectedProgramId &&
        !!selectedLembagaId,
      select(data) {
        return {
          ...data,
          data: data?.data?.map?.((item) => ({
            ...item,
            key: `${item.nuptk}_${item.email}_${item.nama}`,
          })),
        }
      },
    },
    permanentFilter: [
      {
        field: 'pageSize',
        operator: 'eq',
        value: -1,
      },
      {
        field: 'programId',
        operator: 'eq',
        value: selectedProgramId,
      },
      {
        field: 'lembagaId',
        operator: 'eq',
        value: selectedLembagaId,
      },
    ],
  })
  const { selectProps: selectProgramProps, queryResult: queryProgramResult } =
    useSelect({
      dataProviderName: 'lms',
      resource: `diklat/programs`,
      queryOptions: { enabled: modalProps.visible },
      optionLabel: 'name',
      optionValue: 'id',
      defaultValueQueryOptions: {
        enabled: false,
      },
      filters: [
        {
          field: 'pageSize',
          operator: 'eq',
          value: -1,
        },
      ],
    })
  const { selectProps: selectNPSNProps, queryResult: queryNPSNResult } =
    useSelect({
      dataProviderName: 'lms',
      resource: `diklat/admin/my-npsn`,
      queryOptions: {
        enabled:
          modalProps.visible && selectedSource === SUMBER_DATA_DIKLAT.DAPODIK,
      },
      optionLabel: 'namaSekolah',
      optionValue: 'npsn',
      defaultValueQueryOptions: {
        enabled: false,
      },
      filters: [
        {
          field: 'pageSize',
          operator: 'eq',
          value: -1,
        },
      ],
    })
  const { selectProps: selectDinasProps, queryResult: queryDinasResult } =
    useSelect({
      dataProviderName: 'lms',
      resource: `lembaga-simtendik`,
      queryOptions: {
        enabled:
          modalProps.visible && selectedSource === SUMBER_DATA_DIKLAT.SIMTENDIK,
      },
      optionLabel: 'nama',
      optionValue: 'lembagaId',
      defaultValueQueryOptions: {
        enabled: false,
      },
      filters: [
        {
          field: 'pageSize',
          operator: 'eq',
          value: -1,
        },
      ],
    })

  const shouldDisableButton =
    !selectedProgramId ||
    (selectedSource === SUMBER_DATA_DIKLAT.DAPODIK && !selectedNPSN) ||
    (selectedSource === SUMBER_DATA_DIKLAT.SIMTENDIK && !selectedLembagaId)

  return (
    <Modal
      {...modalProps}
      onCancel={onClose}
      title="Ajukan Calon Peserta Didik"
      okText="Ajukan Peserta"
      cancelText="Batal"
      maskClosable={false}
      afterClose={() => {
        formProps?.form?.resetFields()
      }}
      centered
      okButtonProps={{
        ...modalProps.okButtonProps,
        disabled: isUploading.size > 0,
      }}
    >
      {!selectedProgramId && (
        <Alert
          showIcon
          type="warning"
          message="Pilih program terlebih dahulu sebelum mengajukan calon peserta diklat."
          className="!mb-4"
        />
      )}
      <Form
        {...formProps}
        layout="vertical"
        validateMessages={{ required: '${label} harus diisi.' }}
        onFinish={async ({ lembagaId: _, ...values }) => {
          await formProps?.onFinish?.(values)
          onSuccess?.()
        }}
      >
        <Form.Item
          label="Program"
          name="programId"
          required
          rules={[{ required: true }]}
          getValueFromEvent={(data) => {
            formProps?.form?.resetFields(['peserta'])
            return data
          }}
        >
          <Select
            {...selectProgramProps}
            options={[
              ...(queryProgramResult?.data?.data?.map((item) => {
                const hasDraftPL =
                  ((item as DiklatProgram).statusProgramLocal.find(
                    ({ status }) => status === PROGRAM_LOCAL_STATE.DRAFT,
                  )?.count || 0) > 0
                const label = hasDraftPL ? (
                  item.name
                ) : (
                  <>
                    {`${item.name}`}
                    <TextField
                      value="(Semua Program Lokal sudah berjalan)"
                      italic
                      type="secondary"
                      className="ml-2"
                    />
                  </>
                )
                return {
                  label,
                  value: item.id,
                  disabled: !hasDraftPL,
                }
              }) || []),
            ]}
            loading={queryProgramResult.isFetching}
            onSearch={() => {}}
            placeholder="Pilih program"
            filterOption={(input, option) =>
              (option!.label as unknown as string)
                .toLowerCase?.()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item
          label="Sumber Data"
          name="sumberData"
          required
          getValueFromEvent={(data) => {
            formProps?.form?.resetFields(['peserta'])
            formProps?.form?.setFieldsValue({
              npsn: undefined,
              lembagaId: undefined,
            })
            return data.target.value
          }}
          initialValue={SUMBER_DATA_DIKLAT.DAPODIK}
        >
          <Radio.Group>
            <Radio value={SUMBER_DATA_DIKLAT.DAPODIK}>DAPODIK</Radio>
            <Radio value={SUMBER_DATA_DIKLAT.SIMTENDIK}>SIMTENDIK</Radio>
          </Radio.Group>
        </Form.Item>
        {selectedSource === SUMBER_DATA_DIKLAT.DAPODIK && (
          <Form.Item
            label="Sekolah (NPSN)"
            name="npsn"
            required
            rules={[{ required: true }]}
            getValueFromEvent={(data) => {
              formProps?.form?.resetFields(['peserta'])
              return data
            }}
          >
            <Select
              {...selectNPSNProps}
              options={[
                ...(selectNPSNProps?.options?.map(({ label, value }) => ({
                  label: `${label} (${value})`,
                  value,
                })) || []),
              ]}
              loading={queryNPSNResult.isFetching}
              onSearch={() => {}}
              placeholder="Pilih sekolah"
              filterOption={(input, option) =>
                (option!.label as unknown as string)
                  .toLowerCase?.()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        )}
        {selectedSource === SUMBER_DATA_DIKLAT.SIMTENDIK && (
          <>
            <Form.Item
              label="Dinas"
              name="lembagaId"
              required
              rules={[{ required: true }]}
              getValueFromEvent={(data) => {
                formProps?.form?.resetFields(['peserta'])
                return data
              }}
            >
              <Select
                {...selectDinasProps}
                loading={queryDinasResult.isFetching}
                onSearch={() => {}}
                placeholder="Pilih dinas"
                filterOption={(input, option) =>
                  (option!.label as unknown as string)
                    .toLowerCase?.()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item hidden initialValue={'00000001'} name="npsn" />
          </>
        )}
        <Form.List
          name="peserta"
          rules={[
            {
              validator(
                _,
                value: {
                  nama: string
                  email: string
                  nuptk: string
                  fileUrl?: string
                }[],
              ) {
                if (!value?.length)
                  return Promise.reject(
                    'Daftar calon peserta yang diajukan tidak boleh kosong.',
                  )

                /** Validate nuptk is not duplicated */
                const nuptkList = (value || [])
                  .map((item) => item?.nuptk?.toString?.().trim?.())
                  .filter((n) => !!n)
                if (new Set(nuptkList).size !== nuptkList.length)
                  return Promise.reject('NUPTK tidak boleh duplikat.')

                /** Validate nuptk already registered inside the program */
                const registeredNuptk = getRegisteredNuptk({
                  candidates: value,
                  registeredParticipants:
                    selectedSource === SUMBER_DATA_DIKLAT.DAPODIK
                      ? dapodikTableQueryResult
                      : simtendikTableQueryResult,
                })
                if (registeredNuptk) {
                  return Promise.reject(
                    `NUPTK "${registeredNuptk}" telah terdaftar pada program`,
                  )
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          {(fields, { add, remove }, { errors }) => {
            return (
              <>
                <Card
                  title={
                    <div className="before:content-['*'] before:text-[#a61d24] before:mr-1">
                      Daftar calon peserta didik yang diajukan
                    </div>
                  }
                  size="small"
                  extra={
                    <Space>
                      <Button
                        type="default"
                        onClick={() => setShowCandidateModal(selectedSource)}
                        icon={<Icons.UsergroupAddOutlined />}
                        disabled={shouldDisableButton}
                      >
                        Daftar Kandidat Peserta
                      </Button>
                    </Space>
                  }
                >
                  <div className="flex flex-col gap-2">
                    <Table
                      rowKey="key"
                      size="small"
                      pagination={false}
                      dataSource={fields}
                      footer={() => (
                        <Button
                          type="text"
                          onClick={() => add()}
                          icon={<Icons.PlusOutlined />}
                          size="small"
                          className="!m-0"
                          disabled={shouldDisableButton}
                        >
                          Tambah secara manual
                        </Button>
                      )}
                      columns={[
                        {
                          title: (
                            <div className="before:content-['*'] before:text-[#a61d24] before:mr-1">
                              Nama
                            </div>
                          ),
                          dataIndex: 'nama',
                          className: 'align-top',
                          render(_, { key, name, ...rest }) {
                            return (
                              <Form.Item
                                {...rest}
                                key={key}
                                className="!mb-0"
                                name={[name, 'nama']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Nama harus diisi.',
                                  },
                                ]}
                              >
                                <Input />
                              </Form.Item>
                            )
                          },
                        },
                        {
                          title: (
                            <div className="before:content-['*'] before:text-[#a61d24] before:mr-1">
                              Email
                            </div>
                          ),
                          dataIndex: 'email',
                          className: 'align-top',
                          render(_, { key, name, ...rest }) {
                            return (
                              <Form.Item
                                {...rest}
                                key={key}
                                className="!mb-0"
                                name={[name, 'email']}
                                rules={[
                                  {
                                    required: true,
                                    type: 'email',
                                    message: 'Email tidak valid.',
                                  },
                                ]}
                              >
                                <Input />
                              </Form.Item>
                            )
                          },
                        },
                        {
                          title: 'NUPTK',
                          dataIndex: 'nuptk',
                          className: 'align-top',
                          render(_, { key, name, ...rest }) {
                            return (
                              <Form.Item
                                {...rest}
                                key={key}
                                className="!mb-0"
                                name={[name, 'nuptk']}
                              >
                                <Input />
                              </Form.Item>
                            )
                          },
                        },
                        {
                          title: <div>Nomor HP</div>,
                          dataIndex: 'phone',
                          className: 'align-top',
                          render(_, { key, name, ...rest }) {
                            return (
                              <Form.Item
                                {...rest}
                                key={key}
                                className="!mb-0"
                                name={[name, 'phone']}
                                rules={[
                                  {
                                    validator(_, value) {
                                      return phone(value)
                                    },
                                    message: 'Nomor HP tidak valid.',
                                  },
                                ]}
                              >
                                <Input />
                              </Form.Item>
                            )
                          },
                        },
                        {
                          title: 'Dokumen Pendukung',
                          dataIndex: 'fileUrl',
                          className: 'align-top',
                          render(_, { key, name, ...rest }) {
                            return (
                              <Form.Item
                                {...rest}
                                key={key}
                                className="!mb-0"
                                name={[name, 'fileUrl']}
                              >
                                <UploadSupportingDocument
                                  onUpload={(uploading) => {
                                    setIsUploading((prev) => {
                                      if (uploading) {
                                        return new Set(prev).add(name)
                                      }
                                      const next = new Set(prev)
                                      next.delete(name)
                                      return next
                                    })
                                  }}
                                />
                              </Form.Item>
                            )
                          },
                        },
                        {
                          title: 'Aksi',
                          dataIndex: 'action',
                          className: 'align-top',
                          render(_, { name }) {
                            return (
                              <Button
                                danger
                                onClick={() => remove(name)}
                                disabled={isUploading.has(name)}
                              >
                                Hapus
                              </Button>
                            )
                          },
                        },
                      ]}
                    />
                  </div>
                </Card>
                <div className="w-full mt-1">
                  <Form.ErrorList errors={errors} />
                </div>
                <ModalParticipantCandidateNPSN
                  onCancel={() => setShowCandidateModal(null)}
                  onOk={(candidates) => {
                    candidates.forEach(
                      ({ key: _, registered: __, ...candidate }) => {
                        add({
                          ...candidate,
                        })
                      },
                    )
                  }}
                  visible={showCandidateModal === SUMBER_DATA_DIKLAT.DAPODIK}
                  tableProps={dapodikTableProps}
                />
                <ModalParticipantCandidateDinas
                  onCancel={() => setShowCandidateModal(null)}
                  onOk={(candidates) => {
                    candidates.forEach(
                      ({ key: _, registered: __, ...candidate }) => {
                        add({
                          ...candidate,
                        })
                      },
                    )
                  }}
                  visible={showCandidateModal === SUMBER_DATA_DIKLAT.SIMTENDIK}
                  tableProps={simtendikTableProps}
                />
              </>
            )
          }}
        </Form.List>
      </Form>
    </Modal>
  )
}

export default ModalFormAddCalonPesertaDidik
